import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import repositoryContainer from '../../services/RepositoryContainer';
import pushNotifications from '../../services/pushNotifications';

const AuthContext = createContext(null);

const normalizePlan = (plan) => {
  const raw = String(plan || 'Starter').trim();
  if (!raw) return 'starter';
  return raw.toLowerCase();
};

const PLAN_RANK = {
  starter: 0,
  pro: 1,
  business: 2,
};

const getPlanRank = (plan) => PLAN_RANK[normalizePlan(plan)] ?? 0;

const parseSubscription = (subscriptionData) => {
  if (!subscriptionData || typeof subscriptionData !== 'object') {
    return null;
  }

  return {
    plan: String(subscriptionData.plan || 'Starter'),
    status: String(subscriptionData.status || 'Inactive'),
    currentPeriodEnd: subscriptionData.currentPeriodEnd || null,
    gracePeriodEnd: subscriptionData.gracePeriodEnd || null,
    ...subscriptionData,
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const subscription = useMemo(() => parseSubscription(user?.subscription), [user]);

  const isSubscriptionActive = useMemo(
    () => subscription?.status?.toLowerCase() === 'active',
    [subscription]
  );

  const isStarterPlan = useMemo(
    () => normalizePlan(subscription?.plan) === 'starter',
    [subscription]
  );

  const isProPlan = useMemo(
    () => normalizePlan(subscription?.plan) === 'pro',
    [subscription]
  );

  const isBusinessPlan = useMemo(
    () => normalizePlan(subscription?.plan) === 'business',
    [subscription]
  );

  const isProOrBusiness = useMemo(
    () => getPlanRank(subscription?.plan) >= getPlanRank('Pro'),
    [subscription]
  );

  const canAccessSubscriptionFeature = useCallback(
    (requiredPlans) => {
      if (!subscription) {
        return false;
      }

      if (!Array.isArray(requiredPlans)) {
        requiredPlans = [requiredPlans];
      }

      const currentRank = getPlanRank(subscription.plan);
      return requiredPlans.some((requiredPlan) => currentRank >= getPlanRank(requiredPlan));
    },
    [subscription]
  );

  const clearLocalSession = useCallback(async (shouldNavigate = true) => {
    await pushNotifications.endSession();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    repositoryContainer.setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    if (shouldNavigate) navigate('/admin/auth', { replace: true });
  }, [navigate]);

  const refreshUser = useCallback(async ({ skipAuthRefresh = false } = {}) => {
    try {
      const userData = await repositoryContainer.auth.whoami({ skipAuthRefresh });
      setUser(userData);
      setIsAuthenticated(true);
      return userData;
    } catch (error) {
      console.error('Failed to refresh authenticated user:', error);
      return null;
    }
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!accessToken || !refreshToken) {
      setIsLoading(false);
      return;
    }

    try {
      repositoryContainer.setToken(accessToken);
      const userData = await refreshUser({ skipAuthRefresh: true });
      if (!userData) throw new Error('Authenticated session could not be restored.');
    } catch (error) {
      console.log('Access token expired, attempting refresh...');
      const refreshSuccess = await refreshAccessToken();

      if (!refreshSuccess) {
        await logout(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      return false;
    }

    try {
      const response = await repositoryContainer.auth.refreshToken(refreshToken);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      repositoryContainer.setToken(response.accessToken);
      const userData = await refreshUser({ skipAuthRefresh: true });
      if (!userData) {
        throw new Error('Failed to hydrate authenticated user after token refresh.');
      }
      console.log('Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      await clearLocalSession(true);
      return false;
    }
  };

  const login = async (email, password) => {
    try {
      await pushNotifications.endSession();
      const response = await repositoryContainer.auth.login({ email, password });
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      repositoryContainer.setToken(response.accessToken);
      const userData = await refreshUser();
      if (!userData) {
        throw new Error('Failed to load user after login.');
      }
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.data?.message || 'Login failed. Please check your credentials.',
      };
    }
  };

  const register = async (registrationData) => {
    try {
      await repositoryContainer.auth.register(registrationData);
      const loginResult = await login(registrationData.email, registrationData.password);
      return loginResult;
    } catch (error) {
      return {
        success: false,
        error: error.data?.message || 'Registration failed. Please try again.',
      };
    }
  };

  const logout = async (shouldNavigate = true) => {
    try {
      await pushNotifications.endSession();
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await repositoryContainer.auth.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      await clearLocalSession(shouldNavigate);
    }
  };

  const forgotPassword = async (email) => {
    try {
      await repositoryContainer.auth.forgotPassword(email);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.data?.message || 'Failed to send reset link. Please try again.',
      };
    }
  };

  const resetPassword = async ({ email, token, newPassword }) => {
    try {
      await repositoryContainer.auth.resetPassword({ email, token, newPassword });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.data?.message || 'Failed to reset password. The link may have expired.',
      };
    }
  };

  const value = {
    user,
    setUser,
    subscription,
    isSubscriptionActive,
    isStarterPlan,
    isProPlan,
    isBusinessPlan,
    isProOrBusiness,
    canAccessSubscriptionFeature,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshAccessToken,
    refreshUser,
    forgotPassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
